export const DATAWRAPPER_CHART_TYPES = [
  "d3-lines",
  "d3-area",
  "d3-bars",
  "d3-bars-stacked",
  "column-chart",
  "d3-dot-plot",
  "d3-scatter-plot",
  "d3-pies",
  "tables",
] as const;

export const DATAWRAPPER_PRESETS = [
  "monocolor",
  "classic",
  "two-tone",
  "themed",
] as const;

export type DatawrapperChartType = (typeof DATAWRAPPER_CHART_TYPES)[number];
export type DatawrapperPreset = (typeof DATAWRAPPER_PRESETS)[number];
export type DatawrapperCell = string | number | boolean | null;

export interface DatawrapperDataset {
  columns: string[];
  rows: DatawrapperCell[][];
}

export interface ArticleDatawrapperAsset {
  version: 1;
  assetId: string;
  documentId: string;
  providerId: string;
  ownershipToken: string;
  title: string;
  chartType: DatawrapperChartType;
  preset: DatawrapperPreset;
  colors: string[];
  data: DatawrapperDataset;
  publicUrl: string | null;
  embedHtml: string | null;
  publicVersion: number;
  publishStatus: "draft" | "published";
  syncStatus: "local" | "syncing" | "ready" | "degraded" | "error";
  syncError: string | null;
  lastSyncedAt: string | null;
}

export function createLocalDatawrapperAsset(input: {
  documentId: string;
  title: string;
  chartType: DatawrapperChartType;
  preset: DatawrapperPreset;
  colors: string[];
  data: DatawrapperDataset;
}): ArticleDatawrapperAsset {
  return {
    version: 1,
    assetId: `datawrapper-${crypto.randomUUID()}`,
    documentId: input.documentId,
    providerId: "",
    ownershipToken: "",
    title: input.title,
    chartType: input.chartType,
    preset: input.preset,
    colors: input.colors,
    data: input.data,
    publicUrl: null,
    embedHtml: null,
    publicVersion: 0,
    publishStatus: "draft",
    syncStatus: "local",
    syncError: null,
    lastSyncedAt: null,
  };
}

export function normalizeDatawrapperAsset(
  value: unknown,
): ArticleDatawrapperAsset | null {
  if (!isRecord(value)) return null;
  const data = normalizeDataset(value.data);
  const chartType = readChartType(value.chartType);
  const preset = readPreset(value.preset);
  if (
    value.version !== 1 ||
    typeof value.assetId !== "string" ||
    typeof value.documentId !== "string" ||
    typeof value.title !== "string" ||
    !data ||
    !chartType ||
    !preset
  )
    return null;
  const publicUrl = readPublicUrl(value.publicUrl);
  return {
    version: 1,
    assetId: value.assetId,
    documentId: value.documentId,
    providerId: readString(value.providerId),
    ownershipToken: readString(value.ownershipToken),
    title: value.title.slice(0, 180),
    chartType,
    preset,
    colors: readColors(value.colors),
    data,
    publicUrl,
    embedHtml: typeof value.embedHtml === "string" ? value.embedHtml : null,
    publicVersion:
      typeof value.publicVersion === "number" ? value.publicVersion : 0,
    publishStatus: publicUrl ? "published" : "draft",
    syncStatus: readSyncStatus(value.syncStatus),
    syncError: typeof value.syncError === "string" ? value.syncError : null,
    lastSyncedAt:
      typeof value.lastSyncedAt === "string" ? value.lastSyncedAt : null,
  };
}

export function parseDatawrapperCsv(value: string): DatawrapperDataset {
  const rows = parseCsvRows(value.trim());
  const columns = rows.shift()?.map((cell) => String(cell).trim()) ?? [];
  if (columns.length < 2 || columns.some((column) => !column))
    throw new Error("Use a header row with at least two named columns.");
  if (rows.length === 0) throw new Error("Add at least one data row.");
  if (rows.some((row) => row.length !== columns.length))
    throw new Error("Every data row must match the header column count.");
  return {
    columns,
    rows: rows.map((row) => row.map(readCsvCell)),
  };
}

export function datawrapperDatasetToCsv(data: DatawrapperDataset): string {
  return [data.columns, ...data.rows]
    .map((row) => row.map(writeCsvCell).join(","))
    .join("\n");
}

export function datawrapperThemeColors(): string[] {
  if (typeof document === "undefined") return ["#d4af37", "#f0ead6"];
  const style = getComputedStyle(document.documentElement);
  return [
    readHex(style.getPropertyValue("--fintheon-accent"), "#d4af37"),
    readHex(style.getPropertyValue("--fintheon-text"), "#f0ead6"),
  ];
}

export function extractDatawrapperAssets(
  document: Record<string, unknown>,
): ArticleDatawrapperAsset[] {
  const assets: ArticleDatawrapperAsset[] = [];
  walkNode(document, (node) => {
    if (node.type !== "articleDatawrapper" || !isRecord(node.attrs)) return;
    const asset = normalizeDatawrapperAsset(node.attrs.payload);
    if (asset) assets.push(asset);
  });
  return assets.slice(0, 50);
}

function normalizeDataset(value: unknown): DatawrapperDataset | null {
  if (
    !isRecord(value) ||
    !Array.isArray(value.columns) ||
    !Array.isArray(value.rows)
  )
    return null;
  const columns = value.columns.filter(
    (column): column is string => typeof column === "string" && Boolean(column),
  );
  if (columns.length !== value.columns.length || columns.length < 2)
    return null;
  const rows = value.rows
    .filter(Array.isArray)
    .map((row) =>
      row.map((cell) =>
        cell === null ||
        typeof cell === "string" ||
        typeof cell === "number" ||
        typeof cell === "boolean"
          ? cell
          : String(cell),
      ),
    );
  if (
    rows.length !== value.rows.length ||
    rows.some((row) => row.length !== columns.length)
  )
    return null;
  return { columns, rows };
}

function parseCsvRows(value: string): string[][] {
  if (!value) return [];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    if (char === '"') {
      if (quoted && value[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && value[index + 1] === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += char;
  }
  if (quoted) throw new Error("Close the quoted CSV value before inserting.");
  row.push(cell);
  rows.push(row);
  return rows;
}

function readCsvCell(value: string): DatawrapperCell {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) && /^[-+]?(\d+\.?\d*|\.\d+)$/.test(trimmed)
    ? numeric
    : value;
}

function writeCsvCell(value: DatawrapperCell): string {
  if (value === null) return "";
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function readPublicUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "datawrapper.dwcdn.net"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function readChartType(value: unknown): DatawrapperChartType | null {
  return DATAWRAPPER_CHART_TYPES.find((type) => type === value) ?? null;
}

function readPreset(value: unknown): DatawrapperPreset | null {
  return DATAWRAPPER_PRESETS.find((preset) => preset === value) ?? null;
}

function readSyncStatus(value: unknown): ArticleDatawrapperAsset["syncStatus"] {
  return value === "syncing" ||
    value === "ready" ||
    value === "degraded" ||
    value === "error"
    ? value
    : "local";
}

function readColors(value: unknown): string[] {
  if (!Array.isArray(value)) return ["#d4af37", "#f0ead6"];
  const colors = value.filter(
    (color): color is string =>
      typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color),
  );
  return colors.length > 0 ? colors.slice(0, 12) : ["#d4af37", "#f0ead6"];
}

function readHex(value: string, fallback: string): string {
  const normalized = value.trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback;
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function walkNode(
  node: Record<string, unknown>,
  visitor: (node: Record<string, unknown>) => void,
): void {
  visitor(node);
  if (!Array.isArray(node.content)) return;
  node.content.forEach((child) => {
    if (isRecord(child)) walkNode(child, visitor);
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
